import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Film, Music, Upload, Play, Loader2, Sparkles, Zap, X, Trash2, ExternalLink, Copy, Youtube, Video, FileText, Check, Pencil, Search } from "lucide-react";
import { api } from "@/lib/api";
import { EditProjectModal, DownloadButton } from "@/components/ShowoEditKit";
import { FeaturedBanner, FilmRow, filmThumb } from "@/components/ShowoRows";

// Fliboe Showo — shorts / films / series / music videos.
// Isolated auth: token stored under `fliboe_showo_token` so Showo, FM, Flip,
// Astro, Fixo, and Casino sessions never bleed. Reuses /api/auth/* endpoints.

const SHOWO_TOKEN_KEY = "fliboe_showo_token";
const SHOWO_TIER_KEY  = "fliboe_showo_intended_tier"; // remember tier picked at signup

// Bootstrap Showo session from the main FM token when it exists. Fliboe admins
// and existing Flip authors don't need to sign in twice — they already have a
// valid session under `fliboe_token`. Purely local copy, no API call.
function bootstrapShowoToken() {
  try {
    if (typeof localStorage === "undefined") return;
    if (!localStorage.getItem(SHOWO_TOKEN_KEY) && localStorage.getItem("fliboe_token")) {
      localStorage.setItem(SHOWO_TOKEN_KEY, localStorage.getItem("fliboe_token"));
    }
  } catch (_) {}
}
if (typeof window !== "undefined") bootstrapShowoToken();

const authHeaders = () => {
  bootstrapShowoToken();
  const t = localStorage.getItem(SHOWO_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Self-heal 401 "Invalid session" — Showo keeps its own copy of the FM token.
// If the Showo copy went stale (user re-logged in on FM, or token rotated),
// resync from fliboe_token and ask the retry callback to run again.
// Returns true if a retry should be attempted, false if the user truly needs to re-login.
async function handleSessionError(e) {
  const status = e?.response?.status;
  const detail = (e?.response?.data?.detail || "").toLowerCase();
  if (status !== 401) return false;
  try {
    const main = typeof localStorage !== "undefined" ? localStorage.getItem("fliboe_token") : null;
    const showo = typeof localStorage !== "undefined" ? localStorage.getItem(SHOWO_TOKEN_KEY) : null;
    if (main && main !== showo) {
      localStorage.setItem(SHOWO_TOKEN_KEY, main);
      return true; // caller can retry with fresh token
    }
    // Main token itself is stale — purge and redirect to login so user re-auths.
    try { localStorage.removeItem(SHOWO_TOKEN_KEY); } catch (_) {}
    try { localStorage.removeItem("fliboe_token"); } catch (_) {}
    if (typeof window !== "undefined") {
      alert(detail.includes("expired") ? "Session expired — please sign in again." : "Your session is no longer valid — please sign in again.");
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
    }
  } catch (_) {}
  return false;
}

const CATEGORY_ICON = { music_video: Music, short: Film, feature: Film, series: Film, documentary: Film, international: Film };

function yt_id(u) {
  if (!u) return null;
  const m = u.match(/(?:youtu\.be\/|v=|youtube\.com\/embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

// ────────────────────────────────────────────────────────────
// Landing / Discover
// ────────────────────────────────────────────────────────────
export function ShowoLanding() {
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/showo/films", { params: { limit: 200 } })
      .then(r => setFilms(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const cats = [
    { id: "", label: "All" },
    { id: "short", label: "Shorts" },
    { id: "music_video", label: "Music Videos" },
    { id: "series", label: "Series" },
    { id: "feature", label: "Features" },
    { id: "documentary", label: "Documentaries" },
    { id: "international", label: "International" },
  ];
  const catOf = (f) => (f.category === "shorts" ? "short" : f.category);
  const q = query.trim().toLowerCase();
  const gridFilms = films.filter(f => (!category || catOf(f) === category) &&
    (!q || [f.title, f.genre, f.director, f.artist, f.synopsis].some(v => (v || "").toLowerCase().includes(q))));
  const showGrid = !!(category || q);
  const trending = [...films].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 20);
  const featured = trending.find(f => filmThumb(f)) || trending[0];

  const isSignedIn = !!localStorage.getItem(SHOWO_TOKEN_KEY);
  return (
    <main className="min-h-screen bg-[#08081a] text-white relative" data-testid="showo-landing">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <img src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/60 via-[#08081a]/85 to-[#08081a]" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse at top left, rgba(16,185,129,0.18), transparent 55%), radial-gradient(ellipse at bottom right, rgba(236,72,153,0.15), transparent 55%)" }} />
      </div>
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/showo" className="flex items-center gap-2" data-testid="showo-brand">
            <Film className="w-5 h-5 text-pink-400" />
            <span className="fl-display text-lg font-black tracking-tight">Showo <span className="text-pink-400 text-xs font-semibold uppercase tracking-widest ml-1">by Fliboe</span></span>
          </Link>
          <div className="flex items-center gap-2">
            {isSignedIn
              ? <>
                  <Link to="/showo/make" className="fl-btn !bg-emerald-500 !text-black !py-1.5 !px-3 text-xs" data-testid="showo-make-nav"><Sparkles className="w-3.5 h-3.5" /> Make</Link>
                  <Link to="/showo/studio" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-studio-nav"><Upload className="w-3.5 h-3.5" /> Publish</Link>
                  <button
                    onClick={() => {
                      // Full logout: nuke the Showo token, the fallback main
                      // Fliboe token (otherwise `bootstrapShowoToken` will
                      // silently re-copy it on next load), and the intended-
                      // tier hint. Everything else stays intact.
                      localStorage.removeItem(SHOWO_TOKEN_KEY);
                      localStorage.removeItem(SHOWO_TIER_KEY);
                      localStorage.removeItem("fliboe_token");
                      window.location.href = "/showo";
                    }}
                    className="fl-btn ghost !py-1.5 !px-3 text-xs"
                    data-testid="showo-logout"
                  >Log out</button>
                </>
              : <>
                  <Link to="/showo/login" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-nav-login">Sign in</Link>
                  <Link to="/showo/login?mode=signup" className="fl-btn !bg-pink-500 !text-black !py-1.5 !px-3 text-xs" data-testid="showo-nav-signup">Get started</Link>
                </>
            }
          </div>
        </div>
      </header>

      {featured && !showGrid ? <FeaturedBanner film={featured} /> : (
        <section className="relative z-10 max-w-6xl mx-auto px-5 pt-10">
          <div className="fl-mono text-[11px] tracking-widest uppercase text-pink-400">// Showo · shorts, series, music videos, indie films</div>
          <h1 className="fl-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight">Watch it. Tip it. Share it.</h1>
          <p className="text-slate-300 mt-3 max-w-2xl">Every film spins up its own Pulse SEO site so the world can find it. Creators keep 100% of tips.</p>
        </section>
      )}

      <section className="relative z-10 max-w-6xl mx-auto px-5 pt-4 pb-2">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search titles, genres, creators…" className="fl-input w-full !pl-9" data-testid="showo-search" />
        </div>
        <div className="mt-3 flex gap-2 overflow-x-auto [scrollbar-width:none] pb-1" data-testid="showo-cats">
          {cats.map(c => (
            <button key={c.id || "all"} onClick={() => setCategory(c.id)}
              className={`fl-btn !py-1.5 !px-3 text-xs whitespace-nowrap ${category === c.id ? "!bg-pink-500 !text-black" : "ghost"}`}
              data-testid={`showo-cat-${c.id || "all"}`}>{c.label}</button>
          ))}
        </div>
      </section>

      {!loading && films.length > 0 && !showGrid && (
        <div className="pb-16" data-testid="showo-rows">
          <FilmRow id="trending" title="Trending now" films={trending} Card={FilmCard} />
          <FilmRow id="new" title="New releases" films={films.slice(0, 20)} Card={FilmCard} />
          {cats.filter(c => c.id).map(c => <FilmRow key={c.id} id={c.id} title={c.label} films={films.filter(f => catOf(f) === c.id)} Card={FilmCard} />)}
        </div>
      )}

      <section className={`relative z-10 max-w-6xl mx-auto px-5 pb-16 pt-4 ${!loading && films.length > 0 && !showGrid ? "hidden" : ""}`}>
        {loading ? (
          <div className="text-white/60 fl-mono text-sm">Loading films…</div>
        ) : showGrid && gridFilms.length === 0 ? (
          <div className="text-white/60 text-sm" data-testid="showo-no-results">No films match. Try another search or category.</div>
        ) : films.length === 0 ? (
          <div className="fl-card p-8 bg-white/[0.02] border-white/10 text-center" data-testid="showo-empty">
            <Film className="w-8 h-8 text-pink-400 mx-auto" />
            <div className="fl-display text-2xl font-black mt-3">Nothing here yet — be first.</div>
            <p className="text-slate-400 mt-2">Upload your first short, music video or feature and Showo builds a Pulse SEO site around it, free.</p>
            <button onClick={() => nav("/showo/studio")} className="fl-btn !bg-pink-500 !text-black mt-4" data-testid="showo-empty-publish"><Upload className="w-4 h-4" /> Publish now</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="showo-grid">
            {gridFilms.map(f => <FilmCard key={f.film_id} film={f} />)}
          </div>
        )}
      </section>
    </main>
  );
}

function FilmCard({ film }) {
  const Icon = CATEGORY_ICON[film.category] || Film;
  const ytId = yt_id(film.youtube_url);
  const thumb = film.cover_url || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null);
  return (
    <Link to={`/showo/f/${film.film_id}`} className="block group" data-testid={`showo-card-${film.film_id}`}>
      <div className="relative aspect-video rounded-xl overflow-hidden bg-white/[0.04] border border-white/10 group-hover:border-pink-400/40 transition">
        {thumb ? (
          <img src={thumb} alt={film.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
        ) : (
          <div className="w-full h-full grid place-items-center"><Icon className="w-10 h-10 text-white/30" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent" />
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[10px] fl-mono uppercase tracking-widest">
          <span className="text-pink-300">{(film.category || "").replace("_", " ")}</span>
        </div>
        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur grid place-items-center opacity-0 group-hover:opacity-100 transition"><Play className="w-4 h-4 text-white fill-white" /></div>
      </div>
      <div className="mt-2 font-semibold text-white line-clamp-1">{film.title}</div>
      <div className="text-xs text-slate-400 line-clamp-1">
        {film.category === "music_video" ? (film.artist || "Artist") : (film.director || "Director unknown")}
      </div>
    </Link>
  );
}

// ────────────────────────────────────────────────────────────
// Studio — publish a film + auto-Pulse
// ────────────────────────────────────────────────────────────
export function ShowoStudio() {
  const nav = useNavigate();
  const [tab, setTab] = useState("new"); // new | mine
  const [config, setConfig] = useState(null);
  const [mine, setMine] = useState([]);
  useEffect(() => {
    api.get("/showo/config").then(r => setConfig(r.data));
    if (localStorage.getItem("fliboe_token")) {
      api.get("/showo/films", { params: { mine: true }, headers: authHeaders() }).then(r => setMine(r.data || [])).catch(() => {});
    }
  }, []);
  if (!localStorage.getItem(SHOWO_TOKEN_KEY)) {
    return (
      <main className="min-h-screen bg-[#08081a] text-white grid place-items-center p-6">
        <div className="fl-card p-6 max-w-md">
          <h2 className="fl-display text-2xl font-black">Sign in to publish on Showo</h2>
          <p className="text-slate-400 mt-2 text-sm">Showo has its own login so nothing leaks between apps. Free to start.</p>
          <Link to="/showo/login?mode=signup&next=/showo/studio" className="fl-btn !bg-pink-500 !text-black mt-4" data-testid="showo-signin">Sign up / Sign in</Link>
        </div>
      </main>
    );
  }
  return (
    <main className="min-h-screen bg-[#08081a] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/showo" className="flex items-center gap-2"><Film className="w-5 h-5 text-pink-400" /><span className="fl-display font-black">Showo Studio</span></Link>
          <div className="flex gap-2 items-center flex-wrap">
            <button onClick={() => setTab("new")} className={`fl-btn !py-1.5 !px-3 text-xs ${tab === "new" ? "!bg-pink-500 !text-black" : "ghost"}`} data-testid="showo-tab-new">Publish new</button>
            <button onClick={() => setTab("mine")} className={`fl-btn !py-1.5 !px-3 text-xs ${tab === "mine" ? "!bg-pink-500 !text-black" : "ghost"}`} data-testid="showo-tab-mine">My films ({mine.length})</button>
            <button
              onClick={() => { localStorage.removeItem(SHOWO_TOKEN_KEY); localStorage.removeItem(SHOWO_TIER_KEY); localStorage.removeItem("fliboe_token"); window.location.href = "/showo"; }}
              className="fl-btn ghost !py-1.5 !px-3 text-xs"
              data-testid="showo-studio-logout"
            >Log out</button>
          </div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-5 py-8">
        {tab === "new" ? <PublishForm config={config} onDone={(f) => { setMine(m => [f, ...m]); nav(`/showo/f/${f.film_id}`); }} /> : <MyFilms films={mine} categories={config?.categories} onDelete={(id) => setMine(m => m.filter(x => x.film_id !== id))} />}
      </div>
    </main>
  );
}

function PublishForm({ config, onDone }) {
  const [form, setForm] = useState({
    title: "", category: "short", synopsis: "", genre: "", director: "",
    artist: "", song_title: "", cast: "",
    youtube_url: "", video_url: "", cover_url: "",
    price_cents: 0, tier: "tip", tip_jar_url: "", auto_pulse: true,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isMusic = form.category === "music_video";

  const upload = async (file) => {
    if (!file) return;
    // Cloudflare / ingress in front of production caps request body at ~100 MB.
    // Reject anything above 95 MB early so users don't wait then see a 502.
    if (file.size > 95 * 1024 * 1024) {
      setErr(`Video too large (${Math.round(file.size / (1024 * 1024))} MB). Trim it under 95 MB or upload a compressed version.`);
      return;
    }
    setUploading(true); setUploadPct(0); setErr("");
    const doUpload = async () => {
      const fd = new FormData(); fd.append("video", file);
      return api.post("/showo/upload", fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        timeout: 600000,
        onUploadProgress: (e) => e.total && setUploadPct(Math.round((e.loaded / e.total) * 100)),
      });
    };
    try {
      let r;
      try { r = await doUpload(); }
      catch (ex) {
        if (await handleSessionError(ex)) r = await doUpload();
        else throw ex;
      }
      setForm(f => ({ ...f, video_url: r.data.video_url }));
    } catch (e) { setErr(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setErr("");
    if (!form.title.trim() || form.title.trim().length < 2) { setErr("Title is required"); return; }
    if (!form.youtube_url && !form.video_url) { setErr("Add a YouTube link OR upload a video"); return; }
    if (isMusic && !form.artist) { setErr("Artist is required for music videos"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        cast: form.cast.split(",").map(s => s.trim()).filter(Boolean).slice(0, 12),
        price_cents: Number(form.price_cents) || 0,
      };
      const doPost = () => api.post("/showo/films", payload, { headers: authHeaders() });
      let r;
      try { r = await doPost(); }
      catch (ex) {
        if (await handleSessionError(ex)) r = await doPost();
        else throw ex;
      }
      onDone(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target?.value ?? e }));

  return (
    <div className="fl-card p-6 bg-white/[0.02] border-white/10" data-testid="showo-publish-form">
      <div className="fl-mono text-[11px] tracking-widest uppercase text-pink-400">// New film</div>
      <h2 className="fl-display text-2xl font-black mt-1">Tell us what you made.</h2>
      <div className="mt-4 grid gap-3">
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Category</span>
          <select value={form.category} onChange={set("category")} className="fl-input w-full mt-1" data-testid="showo-category">
            {(config?.categories || []).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Title</span>
          <input value={form.title} onChange={set("title")} className="fl-input w-full mt-1" placeholder={isMusic ? "e.g. Neon Rain" : "e.g. Last Bus Home"} maxLength={140} data-testid="showo-title" />
        </label>

        {isMusic ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Artist</span>
              <input value={form.artist} onChange={set("artist")} className="fl-input w-full mt-1" data-testid="showo-artist" /></label>
            <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Song title (if different)</span>
              <input value={form.song_title} onChange={set("song_title")} className="fl-input w-full mt-1" data-testid="showo-song" /></label>
          </div>
        ) : (
          <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Director</span>
            <input value={form.director} onChange={set("director")} className="fl-input w-full mt-1" data-testid="showo-director" /></label>
        )}

        <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Genre</span>
          <input value={form.genre} onChange={set("genre")} className="fl-input w-full mt-1" placeholder="drama, sci-fi, hip-hop…" data-testid="showo-genre" /></label>

        <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Synopsis</span>
          <textarea value={form.synopsis} onChange={set("synopsis")} className="fl-input w-full mt-1 min-h-[100px]" maxLength={2000} data-testid="showo-synopsis" /></label>

        {!isMusic && (
          <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Cast (comma-separated)</span>
            <input value={form.cast} onChange={set("cast")} className="fl-input w-full mt-1" placeholder="Ava Ren, Marcus Cole" data-testid="showo-cast" /></label>
        )}

        <div className="mt-2 border-t border-white/10 pt-4">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-400">// Video source (pick one)</div>
          <label className="block mt-2">
            <span className="text-xs text-white/70 flex items-center gap-1.5"><Youtube className="w-3.5 h-3.5 text-red-400" /> YouTube URL</span>
            <input value={form.youtube_url} onChange={set("youtube_url")} className="fl-input w-full mt-1" placeholder="https://youtu.be/…" data-testid="showo-yt" />
          </label>
          <div className="text-xs text-white/40 text-center my-2 fl-mono">or</div>
          <label className="block">
            <span className="text-xs text-white/70 flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-emerald-400" /> Upload from phone (MP4/MOV, up to 95 MB)</span>
            <label className="mt-1 fl-btn ghost !py-2 !px-3 text-xs cursor-pointer inline-flex" data-testid="showo-upload-btn">
              <Upload className="w-3.5 h-3.5" />
              {uploading ? `Uploading ${uploadPct}%…` : (form.video_url ? "Replace file" : "Choose video")}
              <input type="file" accept="video/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="showo-upload-input" />
            </label>
            {form.video_url && !uploading && (
              <div className="mt-2 text-xs text-emerald-300 fl-mono">✔ Uploaded — will stream on Showo</div>
            )}
          </label>
        </div>

        <label className="mt-3 flex items-start gap-3 p-3 rounded-lg bg-pink-500/10 border border-pink-500/20 cursor-pointer" data-testid="showo-autopulse-toggle">
          <input type="checkbox" checked={form.auto_pulse} onChange={e => setForm(f => ({ ...f, auto_pulse: e.target.checked }))} className="mt-0.5" />
          <div>
            <div className="text-white text-sm font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-pink-300" /> Auto-generate a Pulse SEO site for this film</div>
            <div className="text-xs text-slate-300 mt-0.5">Every film gets its own AI-written blog that drives search traffic. Free during MVP.</div>
          </div>
        </label>

        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Tip Jar URL (optional · you keep 100%)</span>
          <input value={form.tip_jar_url} onChange={set("tip_jar_url")} className="fl-input w-full mt-1" placeholder="https://buymeacoffee.com/you  ·  https://cash.app/$you  ·  https://ko-fi.com/you" data-testid="showo-tipjar" />
          <span className="text-[10px] text-white/40 mt-1 inline-block">Buy Me a Coffee, Cash App, Ko-fi, Venmo, PayPal.me — any link works. Fliboe takes 0%.</span>
        </label>

        {err && <div className="text-red-300 text-sm" data-testid="showo-err">{err}</div>}
        <button onClick={submit} disabled={busy || uploading} className="fl-btn !bg-pink-500 !text-black mt-2" data-testid="showo-publish-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Publishing…" : "Publish film"}
        </button>
      </div>
    </div>
  );
}

function MyFilms({ films, categories, onDelete }) {
  const [editing, setEditing] = useState(null);
  const [editingTip, setEditingTip] = useState(null); // film_id being edited
  const [tipDraft, setTipDraft] = useState("");
  const [savingTip, setSavingTip] = useState(false);
  const [localFilms, setLocalFilms] = useState(films);
  useEffect(() => { setLocalFilms(films); }, [films]);

  const saveTip = async (film_id) => {
    if (!film_id) { alert("Film ID missing — refresh the page and try again."); return; }
    setSavingTip(true);
    try {
      const { data } = await api.patch(`/showo/films/${film_id}`, { tip_jar_url: tipDraft }, { headers: authHeaders() });
      setLocalFilms(fs => fs.map(x => x.film_id === film_id ? { ...x, tip_jar_url: data.tip_jar_url } : x));
      setEditingTip(null); setTipDraft("");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        alert("This film no longer exists in our database — refresh the page. If you just published it, wait a moment and try again.");
      } else if (status === 403) {
        alert("This film isn't yours — sign in with the account that published it.");
      } else if (!status) {
        alert("Backend is temporarily unreachable. Try again in a minute.");
      } else {
        alert(detail || `Save failed (HTTP ${status})`);
      }
    }
    finally { setSavingTip(false); }
  };

  const saveEdit = async (patch) => {
    const { data } = await api.patch(`/showo/films/${editing.film_id}`, patch, { headers: authHeaders() });
    setLocalFilms(fs => fs.map(x => x.film_id === data.film_id ? { ...x, ...data } : x));
  };

  if (!localFilms.length) return <div className="text-white/60 fl-mono text-sm">You haven&apos;t published anything yet.</div>;
  return (
    <div className="grid gap-3" data-testid="showo-my-films">
      {editing && <EditProjectModal project={editing} categories={categories} headers={authHeaders} onSave={saveEdit} onClose={() => setEditing(null)} />}
      {localFilms.map(f => (
        <div key={f.film_id} className="fl-card p-4 bg-white/[0.02] border-white/10">
          <div className="flex flex-wrap items-center gap-3">
            {f.cover_url && <img src={f.cover_url} alt="" className="w-10 h-14 object-cover rounded border border-white/10" />}
            <div className="flex-1 min-w-[160px]">
              <div className="font-semibold text-white" data-testid={`showo-film-title-${f.film_id}`}>{f.title}</div>
              <div className="text-xs text-slate-400">{(f.category || "").replace("_", " ")}{f.genre ? ` · ${f.genre}` : ""} · {f.tip_jar_url ? <span className="text-emerald-300">tip jar ✓</span> : <span className="text-white/40">no tip jar</span>}</div>
            </div>
            <Link to={`/showo/f/${f.film_id}`} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`showo-open-${f.film_id}`}>Open</Link>
            <button onClick={() => setEditing(f)} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`showo-edit-${f.film_id}`}><Pencil className="w-3 h-3" /> Edit</button>
            {f.video_url && <DownloadButton path={`/showo/films/${f.film_id}/download`} title={f.title} headers={authHeaders} testId={`showo-download-${f.film_id}`} />}
            <button onClick={() => { setEditingTip(f.film_id); setTipDraft(f.tip_jar_url || ""); }} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`showo-edit-tip-${f.film_id}`}>{f.tip_jar_url ? "Edit tip" : "Add tip jar"}</button>
            {f.pulse_site_id && <Link to={`/pulse?site=${f.pulse_site_id}`} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`showo-pulse-${f.film_id}`}><FileText className="w-3 h-3" /> Pulse</Link>}
            <button onClick={async () => {
              if (!confirm(`Delete "${f.title}"? This removes its Pulse site too.`)) return;
              await api.delete(`/showo/films/${f.film_id}`, { headers: authHeaders() });
              setLocalFilms(fs => fs.filter(x => x.film_id !== f.film_id));
              onDelete(f.film_id);
            }} className="fl-btn ghost !py-1.5 !px-2 text-xs" data-testid={`showo-del-${f.film_id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
          {editingTip === f.film_id && (
            <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-2 items-center" data-testid={`showo-tip-form-${f.film_id}`}>
              <input value={tipDraft} onChange={e => setTipDraft(e.target.value)} className="fl-input flex-1 min-w-[220px] text-sm" placeholder="https://buymeacoffee.com/you · Cash App · Ko-fi · PayPal.me" data-testid={`showo-tip-input-${f.film_id}`} />
              <button onClick={() => saveTip(f.film_id)} disabled={savingTip} className="fl-btn !py-1.5 !px-3 text-xs !bg-emerald-500 !text-black" data-testid={`showo-tip-save-${f.film_id}`}>{savingTip ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}</button>
              <button onClick={() => { setEditingTip(null); setTipDraft(""); }} className="fl-btn ghost !py-1.5 !px-3 text-xs">Cancel</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Player page — /showo/f/:id
// ────────────────────────────────────────────────────────────
export function ShowoPlayer() {
  const { id } = useParams();
  const [film, setFilm] = useState(null);
  const [err, setErr] = useState("");
  const [batchCount, setBatchCount] = useState(2);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchInfo, setBatchInfo] = useState(null);
  useEffect(() => {
    api.get(`/showo/films/${id}`).then(r => {
      setFilm(r.data);
      // SEO: title + JSON-LD VideoObject + canonical
      if (r.data) {
        document.title = `${r.data.title} — ${r.data.category === "music_video" ? (r.data.artist || "Music Video") : "Showo"}`;
        const canonical = `${window.location.origin}/showo/f/${r.data.film_id || id}`;
        let can = document.querySelector('link[rel="canonical"]');
        if (!can) { can = document.createElement("link"); can.setAttribute("rel", "canonical"); document.head.appendChild(can); }
        can.setAttribute("href", canonical);
        const abs = (u) => (u && u.startsWith("/") ? window.location.origin + u : u);
        const jsonLd = {
          "@context": "https://schema.org",
          "@type": "VideoObject",
          "name": r.data.title,
          "description": r.data.synopsis || `${r.data.title} on Fliboe Showo`,
          "thumbnailUrl": abs(r.data.cover_url),
          "uploadDate": r.data.created_at,
          "contentUrl": abs(r.data.video_url) || r.data.youtube_url,
          "embedUrl": r.data.youtube_url || abs(r.data.video_url),
          "genre": r.data.genre,
          "director": r.data.director ? { "@type": "Person", "name": r.data.director } : undefined,
          "actor": (r.data.cast || []).map(n => ({ "@type": "Person", "name": n })),
          "publisher": { "@type": "Organization", "name": "Fliboe", "logo": { "@type": "ImageObject", "url": window.location.origin + "/icon-512.png" } },
          "interactionStatistic": { "@type": "InteractionCounter", "interactionType": "https://schema.org/WatchAction", "userInteractionCount": r.data.views || 0 },
          "url": canonical,
        };
        let s = document.getElementById("showo-jsonld");
        if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "showo-jsonld"; document.head.appendChild(s); }
        s.textContent = JSON.stringify(jsonLd);
      }
    }).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [id]);

  const runBatch = async () => {
    if (!film?.pulse_site_id) return;
    setBatchBusy(true); setBatchInfo(null);
    try {
      const { data } = await api.post(`/pulse/sites/${film.pulse_site_id}/generate-batch`, { count: batchCount }, { headers: authHeaders() });
      setBatchInfo({ ok: true, spawned: data.spawned, remaining: data.remaining_quota });
    } catch (e) { setBatchInfo({ ok: false, msg: e?.response?.data?.detail || "Batch failed" }); }
    finally { setBatchBusy(false); }
  };

  if (err) return <main className="min-h-screen bg-[#08081a] text-white grid place-items-center"><div className="fl-card p-6">{err}</div></main>;
  if (!film) return <main className="min-h-screen bg-[#08081a] text-white grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></main>;

  const ytId = yt_id(film.youtube_url);
  return (
    <main className="min-h-screen bg-[#08081a] text-white" data-testid="showo-player">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/showo" className="flex items-center gap-2"><Film className="w-5 h-5 text-pink-400" /><span className="fl-display font-black">Showo</span></Link>
          <Link to="/showo/studio" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-player-studio"><Upload className="w-3.5 h-3.5" /> Publish</Link>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="aspect-video rounded-2xl overflow-hidden bg-black border border-white/10">
          {ytId ? (
            <iframe title={film.title} className="w-full h-full" src={`https://www.youtube.com/embed/${ytId}`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
          ) : film.video_url ? (
            <video className="w-full h-full" src={film.video_url} controls playsInline data-testid="showo-video-el" />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/40">No video source</div>
          )}
        </div>

        <div className="mt-5">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">{film.category.replace("_", " ")}</div>
          <h1 className="fl-display text-3xl font-black mt-1" data-testid="showo-title-h1">{film.title}</h1>
          <div className="text-sm text-slate-300 mt-1">
            {film.category === "music_video" ? (
              <>By <strong>{film.artist || "—"}</strong>{film.song_title && ` · "${film.song_title}"`}{film.genre && ` · ${film.genre}`}</>
            ) : (
              <>Directed by <strong>{film.director || "—"}</strong>{film.genre && ` · ${film.genre}`}</>
            )}
          </div>
          {film.synopsis && <p className="mt-3 text-slate-200 leading-relaxed max-w-3xl">{film.synopsis}</p>}
          {film.cast?.length > 0 && <div className="mt-3 text-xs text-slate-400"><strong className="text-white/70">Cast:</strong> {film.cast.join(", ")}</div>}
        </div>

        {/* Tip Jar — "TIP" label on top of a jar icon. 100% goes to creator. */}
        {film.tip_jar_url && (
          <div className="mt-6 fl-card p-5 bg-emerald-500/10 border-emerald-400/30 flex items-center gap-4" data-testid="showo-tipjar-block">
            <a
              href={film.tip_jar_url}
              target="_blank"
              rel="noreferrer"
              className="group relative inline-flex flex-col items-center select-none"
              data-testid="showo-tipjar-btn"
              aria-label="Leave a tip for the creator"
            >
              <span className="fl-mono text-[11px] tracking-[0.4em] font-black text-emerald-300 mb-1 group-hover:text-emerald-200 transition">TIP</span>
              <svg width="60" height="70" viewBox="0 0 60 70" className="text-emerald-400 group-hover:text-emerald-300 transition drop-shadow-lg">
                {/* Jar lid */}
                <rect x="10" y="6" width="40" height="10" rx="2" fill="currentColor" opacity="0.9" />
                <rect x="8"  y="14" width="44" height="4" rx="1" fill="currentColor" opacity="0.7" />
                {/* Jar body */}
                <path d="M12 20 Q10 24 10 30 L10 60 Q10 66 16 66 L44 66 Q50 66 50 60 L50 30 Q50 24 48 20 Z" fill="currentColor" opacity="0.25" stroke="currentColor" strokeWidth="2" />
                {/* Coin slot */}
                <rect x="22" y="24" width="16" height="3" rx="1.5" fill="#0b0b1e" />
                {/* Dollar sign */}
                <text x="30" y="52" textAnchor="middle" fontSize="24" fontWeight="900" fill="currentColor">$</text>
              </svg>
            </a>
            <div>
              <div className="fl-display text-lg font-black text-white">Enjoyed it? Leave a tip.</div>
              <div className="text-sm text-emerald-200 mt-0.5">100% goes to {film.category === "music_video" ? film.artist || "the artist" : film.director || "the filmmaker"}. Fliboe takes 0%.</div>
            </div>
          </div>
        )}

        {film.pulse_site_id ? (
          <div className="mt-8 fl-card p-5 bg-pink-500/10 border-pink-500/20" data-testid="showo-pulse-panel">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-pink-300 mt-0.5" />
              <div className="flex-1">
                <div className="fl-display text-lg font-black">Pulse SEO site for this film</div>
                <p className="text-slate-300 text-sm mt-1">Auto-generate articles that rank on Google and pull viewers to the film.</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <label className="text-xs text-white/70">Articles per batch:</label>
                  {[2, 3, 6].map(n => (
                    <button key={n} onClick={() => setBatchCount(n)}
                      className={`fl-btn !py-1 !px-3 text-xs ${batchCount === n ? "!bg-pink-500 !text-black" : "ghost"}`}
                      data-testid={`showo-batch-${n}`}>{n}/day</button>
                  ))}
                  <button onClick={runBatch} disabled={batchBusy} className="fl-btn !bg-pink-500 !text-black !py-1.5 !px-3 text-xs" data-testid="showo-batch-run">
                    {batchBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} {batchBusy ? "Spawning…" : "Generate now"}
                  </button>
                  <Link to={`/pulse?site=${film.pulse_site_id}`} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-pulse-open">Open Pulse dashboard <ExternalLink className="w-3 h-3" /></Link>
                </div>
                {batchInfo && (
                  <div className={`mt-3 text-sm ${batchInfo.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="showo-batch-result">
                    {batchInfo.ok
                      ? `${batchInfo.spawned} article${batchInfo.spawned > 1 ? "s" : ""} spawned. ${batchInfo.remaining} left in your monthly Pulse quota.`
                      : batchInfo.msg}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 fl-card p-5 bg-white/[0.03] border-white/10" data-testid="showo-pulse-locked">
            <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">// Pulse SEO locked</div>
            <div className="fl-display text-lg font-black mt-1">Add an auto Pulse site to this film</div>
            <p className="text-slate-400 text-sm mt-1">Available on Showo + Pulse ($9.99/mo, 1 site + 2 articles/day) and Showo Pro ($19.99/mo, 3 sites + 6 articles/day + WordPress publish).</p>
            <Link to="/pricing" className="fl-btn !bg-pink-500 !text-black mt-3 !py-1.5 !px-3 text-xs" data-testid="showo-upgrade">Upgrade</Link>
          </div>
        )}

        {/* Share block — creators grab the link and post it wherever */}
        <div className="mt-6 fl-card p-5 bg-white/[0.02] border-white/10" data-testid="showo-share">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60">// Share this film</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              readOnly
              value={`${window.location.origin}/showo/f/${film.film_id}`}
              className="fl-input flex-1 min-w-[220px] text-xs"
              onFocus={e => e.target.select()}
              data-testid="showo-share-url"
            />
            <button
              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/showo/f/${film.film_id}`)}
              className="fl-btn !py-1.5 !px-3 text-xs !bg-white !text-black" data-testid="showo-share-copy">
              <Copy className="w-3.5 h-3.5" /> Copy link
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(`${film.title}${film.artist ? " — " + film.artist : ""} on Showo: ${window.location.origin}/showo/f/${film.film_id}`)}
              className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-share-caption">
              Copy caption
            </button>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${film.title} — watch on Showo: ${window.location.origin}/showo/f/${film.film_id}`)}`} target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-share-x">X</a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/showo/f/${film.film_id}`)}`} target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-share-li">LinkedIn</a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/showo/f/${film.film_id}`)}`} target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-share-fb">Facebook</a>
            <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${film.title} — watch on Showo: ${window.location.origin}/showo/f/${film.film_id}`)}`} target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-share-wa">WhatsApp</a>
          </div>
        </div>
      </div>
    </main>
  );
}


// ────────────────────────────────────────────────────────────
// SHOWO LOGIN / SIGNUP (/showo/login) — isolated auth
// ────────────────────────────────────────────────────────────
const SHOWO_TIERS_UI = [
  { id: "free",        label: "Free",           price: "$0",       tag: "Try it",       perks: ["2 uploads to Showo", "YouTube link OR phone upload", "Shareable link + social buttons"] },
  { id: "showo_pulse", label: "Showo + Pulse",  price: "$9.99/mo", tag: "Most popular", perks: ["Everything in Free", "1 auto Pulse SEO site per film", "2 AI articles per day, per film"] },
  { id: "showo_pro",   label: "Showo Pro",      price: "$19.99/mo", tag: "Pros",        perks: ["Everything in Pulse", "3 auto Pulse sites per film", "6 AI articles per day", "WordPress auto-publish webhook"] },
];

export function ShowoLogin() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const initialMode = params.get("mode") === "signup" ? "signup" : "login";
  const nextPath = params.get("next") || "/showo/studio";
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(initialMode === "signup" ? "tier" : "form"); // tier | form
  const [tier, setTier] = useState(localStorage.getItem(SHOWO_TIER_KEY) || "free");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOk, setForgotOk] = useState(false);

  const goNextAfterAuth = () => {
    // Remember the tier so /showo/studio can trigger checkout (Stripe wiring is P1).
    if (tier && tier !== "free") localStorage.setItem(SHOWO_TIER_KEY, tier);
    else localStorage.removeItem(SHOWO_TIER_KEY);
    navigate(nextPath, { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault?.(); setErr("");
    if (mode === "forgot") {
      if (!email.trim()) { setErr("Enter your email."); return; }
      setBusy(true);
      try { await api.post("/auth/forgot-password", { email }); setForgotOk(true); }
      catch (ex) { setErr(ex?.response?.data?.detail || "Could not send reset email."); }
      finally { setBusy(false); }
      return;
    }
    if (!email.trim() || password.length < 8) { setErr("Email + 8-char password required."); return; }
    setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const body = mode === "signup"
        ? { email, password, name: (name || email.split("@")[0] || "Showo Creator").trim() }
        : { email, password };
      const { data } = await api.post(path, body);
      if (!data.session_token) throw new Error("No token");
      localStorage.setItem(SHOWO_TOKEN_KEY, data.session_token);
      goNextAfterAuth();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map(x => x?.msg || x?.message || JSON.stringify(x)).join(" · ")
        : (typeof d === "string" ? d : (d?.msg || ex?.message || "Sign-in failed."));
      setErr(msg);
    }
    finally { setBusy(false); }
  };

  const googleSignIn = () => {
    if (mode === "signup" && tier && tier !== "free") localStorage.setItem(SHOWO_TIER_KEY, tier);
    const redirect = window.location.origin + "/showo/auth-callback" + (nextPath ? `?next=${encodeURIComponent(nextPath)}` : "");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <main className="min-h-screen bg-[#08081a] text-white" data-testid="showo-login-page">
      <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden style={{ backgroundImage: "radial-gradient(ellipse at top left, rgba(16,185,129,0.20), transparent 55%), radial-gradient(ellipse at bottom right, rgba(20,83,45,0.18), transparent 55%)" }} />
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/showo" className="flex items-center gap-2"><Film className="w-5 h-5 text-pink-400" /><span className="fl-display font-black">Showo</span></Link>
        </div>
      </header>

      <section className="relative z-10 max-w-3xl mx-auto px-5 py-10">
        <div className="fl-mono text-[11px] tracking-widest uppercase text-pink-400 text-center">// Fliboe Showo · your video home</div>
        <h1 className="fl-display text-3xl font-black text-center mt-2">
          {mode === "signup" ? "Create your Showo account" : mode === "forgot" ? "Reset your password" : "Welcome back to Showo"}
        </h1>

        {mode === "signup" && step === "tier" ? (
          <div className="mt-8" data-testid="showo-tier-step">
            <div className="text-slate-300 text-center text-sm mb-5">Pick a plan (you can change or upgrade later).</div>
            <div className="grid md:grid-cols-3 gap-4">
              {SHOWO_TIERS_UI.map(t => {
                const active = tier === t.id;
                return (
                  <button key={t.id} type="button" onClick={() => setTier(t.id)}
                    className={`fl-card p-5 text-left transition ${active ? "bg-pink-500/15 border-pink-400/60 ring-2 ring-pink-400/40" : "bg-white/[0.02] border-white/10 hover:border-white/20"}`}
                    data-testid={`showo-tier-${t.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">{t.tag}</div>
                      {active && <Check className="w-4 h-4 text-pink-300" />}
                    </div>
                    <div className="fl-display text-xl font-black mt-2">{t.label}</div>
                    <div className="text-pink-300 font-semibold mt-1">{t.price}</div>
                    <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                      {t.perks.map((p, i) => (<li key={i} className="flex gap-1.5"><span className="text-pink-400">•</span>{p}</li>))}
                    </ul>
                  </button>
                );
              })}
            </div>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button onClick={() => setStep("form")} className="fl-btn !bg-pink-500 !text-black" data-testid="showo-tier-continue">
                Continue with {SHOWO_TIERS_UI.find(x => x.id === tier)?.label || "Free"} <Sparkles className="w-4 h-4" />
              </button>
            </div>
            <div className="text-center mt-3">
              <button onClick={() => { setMode("login"); setStep("form"); setErr(""); }} className="text-pink-300 text-xs hover:underline" data-testid="showo-tier-to-login">Already have a Showo account? Sign in</button>
            </div>
          </div>
        ) : (
          <div className="max-w-md mx-auto mt-8">
            {mode === "signup" && (
              <div className="mb-4 fl-card p-3 bg-pink-500/5 border-pink-400/20 flex items-center justify-between" data-testid="showo-selected-tier">
                <div>
                  <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">Selected plan</div>
                  <div className="text-sm font-semibold text-white">{SHOWO_TIERS_UI.find(x => x.id === tier)?.label} · {SHOWO_TIERS_UI.find(x => x.id === tier)?.price}</div>
                </div>
                <button onClick={() => setStep("tier")} className="text-pink-300 text-xs hover:underline" data-testid="showo-change-tier">Change</button>
              </div>
            )}
            <form onSubmit={submit} className="fl-card p-6 space-y-3 bg-black/60 border-pink-400/20">
              {mode === "forgot" && forgotOk ? (
                <div className="text-emerald-300 text-sm text-center py-4" data-testid="showo-forgot-ok">
                  If <strong>{email}</strong> matches an account, we&apos;ve emailed a reset link.
                  <button type="button" onClick={() => { setMode("login"); setForgotOk(false); setErr(""); }} className="block mx-auto mt-4 text-pink-300 text-xs hover:underline">Back to sign in</button>
                </div>
              ) : (
                <>
                  {mode === "signup" && (
                    <input className="fl-input w-full" placeholder="Name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="showo-login-name" />
                  )}
                  <input className="fl-input w-full" placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="showo-login-email" />
                  {mode !== "forgot" && (
                    <input className="fl-input w-full" placeholder="Password (8+ chars)" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="showo-login-password" />
                  )}
                  {err && <div className="text-red-400 text-sm" data-testid="showo-login-err">{err}</div>}
                  <button type="submit" disabled={busy} className="fl-btn w-full justify-center !bg-pink-500 !text-black hover:!bg-pink-400" data-testid="showo-login-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {mode === "signup" ? "Create & continue" : mode === "forgot" ? "Send reset email" : "Sign in"}
                  </button>
                  {mode !== "forgot" && (
                    <>
                      <div className="flex items-center gap-3 my-1">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                      </div>
                      <button type="button" onClick={googleSignIn}
                        className="w-full py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-white/90 flex items-center justify-center gap-2 transition"
                        data-testid="showo-login-google">
                        <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                        Continue with Google
                      </button>
                    </>
                  )}
                  <div className="flex items-center justify-between gap-3 mt-2">
                    <button type="button" onClick={() => { const next = mode === "signup" ? "login" : "signup"; setMode(next); setStep(next === "signup" ? "tier" : "form"); setErr(""); }} className="text-pink-300 text-xs hover:underline" data-testid="showo-login-toggle">
                      {mode === "signup" ? "Have an account? Sign in" : mode === "forgot" ? "Back to sign in" : "New here? Create account"}
                    </button>
                    {mode !== "forgot" && (
                      <button type="button" onClick={() => { setMode("forgot"); setErr(""); }} className="text-white/50 text-xs hover:text-pink-300 hover:underline" data-testid="showo-login-forgot">
                        Forgot password?
                      </button>
                    )}
                  </div>
                </>
              )}
            </form>
          </div>
        )}
      </section>
    </main>
  );
}

export function ShowoAuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return; processed.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next") || "/showo/studio";
      if (!m) { navigate("/showo/login", { replace: true }); return; }
      try {
        const { data } = await api.post("/auth/session", { session_id: m[1] });
        if (!data?.session_token) throw new Error("No token");
        localStorage.setItem(SHOWO_TOKEN_KEY, data.session_token);
        window.history.replaceState({}, document.title, next);
        navigate(next, { replace: true });
      } catch (_) { navigate("/showo/login", { replace: true }); }
    })();
  }, [navigate]);
  return (
    <main className="min-h-screen bg-[#08081a] text-white grid place-items-center" data-testid="showo-auth-callback">
      <div className="fl-mono text-xs uppercase tracking-widest text-pink-300">// Signing you in…</div>
    </main>
  );
}
